import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { flatMap } from 'lodash';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

import Layout from '@/components/layout';
import { getAllPartners, getPartnerBySlug } from '@/lib/partners';

export default function Partner({ partner, locale }) {
    // Check if props are present
    if (!partner) {
        return null;
    }

    const { t } = useTranslation('partner');

    const apps = flatMap(partner.apps, app => app);

    const kpiTemplate = (kpis) => {
        if (!kpis) return null;

        return kpis.map((kpi, index) =>
            <div className="card" key={index}>
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="col">
                            {/* <!-- Label --> */}
                            <h6 className="text-uppercase text-muted mb-2">
                                {kpi.label}
                            </h6>
                            {/* <!-- Value --> */}
                            <span className="h2 mb-0">
                                {kpi.value}
                            </span>
                        </div>
                        <div className="col-auto">
                            {/* <!-- Icon --> */}
                            {kpi.icon
                                ? <span className={`h2 fe fe-${kpi.icon} text-muted mb-0`}></span>
                                : <span className="h2 fe fe-calendar text-muted mb-0"></span>
                            }
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const appTemplate = (appList) => {
        if (!appList) return null;

        return appList.map((app, index) =>
            <div className="col-6 col-lg-4" key={app.id}>

                {/*<!-- Card -->*/}
                <div className="card card-fill">
                    <div className="card-body mt-0">

                        {/*<!-- Logo -->*/}
                        <Link href={`/apps/${encodeURIComponent(app.slug)}`}>
                            <a className="card-app-logo mb-4">
                                <img src={app.logo?.url || partner.logo.url} className="product-img" alt="" />
                            </a>
                        </Link>

                        {/*<!-- Title -->*/}
                        <h2 className="card-title">
                            <Link href={`/apps/${encodeURIComponent(app.slug)}`}>
                                <a className="hyphen">{app.name}</a>
                            </Link>
                        </h2>

                        {/*<!-- Text -->*/}
                        <p className="card-text text-muted text-left small">
                            {app[`description_short_${locale}`]}
                        </p>

                    </div>
                    <div className="card-footer card-footer-boxed">
                        <div className="row align-items-center">
                            <div className="col">
                                <Link href={`/apps/${encodeURIComponent(app.slug)}`}>
                                    <a className="btn btn-primary btn-sm btn-block">
                                        <i className="fe fe-info mr-1"></i> Mehr erfahren
                                    </a>
                                </Link>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        );
    };

    const appList = appTemplate(apps);

    return (
        <Layout title={partner.name}>
            {/* <!-- HEADER --> */}
            <div className="header">

                {/* <!-- Image --> */}
                <img
                    src={partner.cover.url}
                    className="header-img-top" alt="..."
                />

                <div className="container">

                    {/* <!-- Body --> */}
                    <div className="header-body mt-n5 mt-md-n6">
                        <div className="row align-items-end">
                            <div className="col-auto">

                                {/* <!-- Avatar --> */}
                                <div className="avatar avatar-xxl header-avatar-top">
                                    <img src={partner.avatar.url} alt="..." className="avatar-img rounded-circle border border-4 border-body" />
                                </div>

                            </div>
                            <div className="col mb-3 ml-n3 ml-md-n2">

                                {/* <!-- Pretitle --> */}
                                <h6 className="header-pretitle">
                                    Partner
                                </h6>

                                {/* <!-- Title --> */}
                                <h1 className="header-title">
                                    {partner.name}
                                </h1>

                            </div>
                            <div className="col-12 col-md-auto mt-2 mt-md-0 mb-md-3">

                                {/* <!-- Button --> */}
                                <Link href={`/contact?partner=${partner.slug}`}>
                                    <a className="btn btn-primary d-block d-md-inline-block">
                                        <i className="fe fe-mail mr-1"></i> Kontakt
                                    </a>
                                </Link>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div className="container">
                <div className="row">
                    <div className="col-12 col-md-8">
                        <div className="card">
                            <div className="card-body">
                                <ReactMarkdown remarkPlugins={[[gfm, { singleTilde: false }]]}>
                                    {partner[`description_long_${locale}`]}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-4">
                        {kpiTemplate(partner.kpi)}
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-md-8">
                        <div className="header">
                            <div className="header-body">

                                {/* <!-- Title --> */}
                                <h1 className="header-title">
                                    Unsere Apps
                                </h1>

                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {appList}
                </div>
            </div>
        </Layout>
    )
}

export async function getStaticPaths({ locales }) {
    const partners = await getAllPartners();

    const paths = locales.reduce(
        (acc, next) => [
            ...acc,
            ...partners.map((partner) => ({
                params: { slug: partner.slug },
                locale: next
            }))
        ],
        []
    );

    return {
        paths,
        fallback: true
        // false => 404
        // https://nextjs.org/docs/basic-features/data-fetching#the-fallback-key-required
    }
}

export async function getStaticProps({ params, locale }) {
    const partner = await getPartnerBySlug(params.slug);

    delete partner.registration_number;

    return {
        // will be passed to the page component as props
        props: {
            partner,
            locale,
            ...await serverSideTranslations(locale, ['common', 'menu', 'partner'])
        },
        revalidate: 1
    }
}
